import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Inbox, Grid3x3, Send, Server, Settings, Users, History, LogOut, Menu, X, FlaskConical, Radio, ArrowLeftRight, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/Auth";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { OfflineBanner } from "@/components/OfflineBanner";

const NAV = [
  { to: "/", label: "Kotak SMS", icon: Inbox, id: "inbox", perm: ["inbox"] },
  { to: "/transfer", label: "Cek Transfer", icon: ArrowLeftRight, id: "transfer", perm: ["transfer"] },
  { to: "/slot", label: "Peta Slot", icon: Grid3x3, id: "slot", perm: ["slots"] },
  { to: "/kirim", label: "Kirim SMS / USSD", icon: Send, id: "send", perm: ["send_sms", "send_ussd"] },
  { to: "/aktivitas", label: "Riwayat Aktivitas", icon: History, id: "activity", perm: ["activity"] },
  { to: "/device", label: "Device", icon: Server, id: "devices", perm: ["devices"] },
  { to: "/akun", label: "Akun & IP", icon: Users, id: "users", admin: true },
  { to: "/ip", label: "Akses IP", icon: ShieldCheck, id: "ip", perm: ["ip_whitelist"], staffOnly: true },
  { to: "/pengaturan", label: "Kode Operator", icon: Settings, id: "settings", perm: ["settings"] },
];

function DemoBanner({ isAdmin, demo }) {
  if (!demo) return null;
  const clear = async () => {
    if (!window.confirm("Hapus semua data demo (device, slot, SMS contoh)?")) return;
    try {
      await api.delete("/demo");
      toast.success("Data demo dihapus");
      window.location.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="demo-banner" className="sticky top-0 z-30 flex items-center gap-3 bg-amber-400 text-white px-4 h-9 text-xs sm:text-sm font-medium">
      <FlaskConical className="h-4 w-4 shrink-0" />
      <span className="truncate"><b>MODE DEMO</b><span className="hidden sm:inline"> — device EJ1-DEMO, SMS, dan pulsa di bawah adalah data contoh, bukan dari device asli.</span><span className="sm:hidden"> · data contoh</span></span>
      {isAdmin && (
        <button data-testid="demo-clear-button" onClick={clear} className="ml-auto shrink-0 underline underline-offset-2 hover:no-underline">
          Hapus demo
        </button>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout, isAdmin, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [demo, setDemo] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/stats").then((r) => setDemo(r.data.demo)).catch(() => {});
  }, []);
  const items = NAV.filter((n) => (n.admin ? isAdmin : n.staffOnly && isAdmin ? false : can(...n.perm)));

  const side = (
    <nav className="flex flex-col gap-1 p-3">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} end data-testid={`nav-${n.id}`} onClick={() => setOpen(false)}
          className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-150 ${isActive ? "bg-emerald-500/10 text-emerald-600 border-l-2 border-emerald-400" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"}`}>
          <n.icon className="h-4 w-4" /> {n.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="grain min-h-screen bg-slate-50">
      <DemoBanner isAdmin={isAdmin} demo={demo} />
      <div className="flex">
        <aside className={`hidden lg:flex w-60 shrink-0 flex-col border-r border-slate-200 bg-white sticky ${demo ? "top-9 h-[calc(100vh-2.25rem)]" : "top-0 h-screen"}`}>
          <Brand />
          {side}
          <UserBox user={user} onLogout={async () => { await logout(); nav("/login"); }} />
        </aside>
        {open && (
          <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setOpen(false)}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <aside className="relative w-64 h-full bg-white border-r border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between pr-3"><Brand /><button data-testid="mobile-nav-close" onClick={() => setOpen(false)}><X className="h-5 w-5 text-slate-500" /></button></div>
              {side}
              <UserBox user={user} onLogout={async () => { await logout(); nav("/login"); }} />
            </aside>
          </div>
        )}
        <main className="relative z-10 flex-1 min-w-0">
          <div className="lg:hidden flex items-center gap-3 border-b border-slate-200 px-4 py-3 bg-white">
            <button data-testid="mobile-nav-open" onClick={() => setOpen(true)}><Menu className="h-5 w-5" /></button>
            <span className="font-display text-xl font-bold">SMS <span className="text-emerald-700">Panel</span></span>
          </div>
          <OfflineBanner canManage={can("devices")} />
          <div className="p-4 md:p-6"><Outlet /></div>
        </main>
      </div>
    </div>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2 px-5 py-5">
      <Radio className="h-5 w-5 text-emerald-700" />
      <span className="font-display text-2xl font-bold tracking-tight">SMS <span className="text-emerald-700">Panel</span></span>
    </div>
  );
}

function UserBox({ user, onLogout }) {
  return (
    <div className="mt-auto border-t border-slate-200 p-4">
      <div className="text-sm font-semibold" data-testid="current-user-name">{user?.name}</div>
      <div className="text-xs text-slate-500 font-mono">{user?.username} · {user?.role === "admin" ? "admin master" : "staf"}</div>
      <div className="text-[11px] text-slate-400 font-mono mt-1">IP {user?.ip}</div>
      <Button data-testid="logout-button" variant="ghost" size="sm" className="mt-3 w-full justify-start text-slate-500" onClick={onLogout}>
        <LogOut className="h-4 w-4 mr-2" /> Keluar
      </Button>
    </div>
  );
}
